import { Link, useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import CopyButton from '@/components/admin/CopyButton';
import { PRODUCT_FIELDS } from '@/shared/fields';
import { committed } from '@/shared/data';
import { PLANS, PRODUCT_TERMS, money, productPrice } from '@/shared/pricing';
import {
  FRONT_STYLES,
  SALE_STYLES,
  featureList,
  frontState,
  isBuiltIn,
  isPrivate,
  productPath,
  saleState,
} from '@/shared/products';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/products/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

export default function AdminProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: product, loading, error, run } = useFetch(
    () => sdk.table('products_c').select(PRODUCT_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/products" label="Back to Products" />;
  const gate = recordGate({ back, loading, record: product, error, run, what: 'Product' });
  if (gate) return gate;

  const builtIn = isBuiltIn(product);
  const privatePlan = isPrivate(product);
  const planLabel = PLANS[product.plan_key_c]?.name ?? product.plan_key_c;
  const path = productPath(product);
  const url = `${window.location.origin}${path}`;
  const features = featureList(product);

  async function toggle(field, next, message) {
    const res = await sdk.table('products_c').update({ Id: product.Id, [field]: next });
    if (!committed(res, message)) return false;
    run();
    return true;
  }

  async function remove() {
    const res = await sdk.table('products_c').remove(product.Id);
    if (!committed(res, 'Product deleted')) return false;
    navigate('/admin/products');
    return true;
  }

  return (
    <div className="mx-auto max-w-4xl">
      {back}
      <PageHeader
        eyebrow={privatePlan ? `Private plan · ${planLabel}` : builtIn ? `Built-in plan · ${planLabel}` : 'Product'}
        title={product.Name}
        description={product.tagline_c}
        actions={
          <>
            <StatusBadge value={frontState(product)} styles={FRONT_STYLES} />
            <StatusBadge value={saleState(product)} styles={SALE_STYLES} />
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/products/${product.Id}/edit`}>
                <ApperIcon name="Pencil" size={16} />
                Edit
              </Link>
            </Button>
            <ConfirmDelete
              title="Delete this product?"
              description={
                privatePlan
                  ? `${planLabel} keeps selling on its private link, and can be added back here with Set up built-in plans.`
                  : builtIn
                    ? 'The plan keeps selling with its default text, and shows on the front page again.'
                    : 'Its page stops working. Orders already placed keep their details.'
              }
              onConfirm={remove}
            />
          </>
        }
      />

      <div className="mb-6 flex flex-wrap gap-2">
        {!privatePlan && (
          <ConfirmAction
            label={product.show_on_home_c ? 'Hide from front page' : 'Show on front page'}
            icon={product.show_on_home_c ? 'EyeOff' : 'Eye'}
            title={product.show_on_home_c ? 'Hide from the front page?' : 'Show on the front page?'}
            description={
              product.show_on_home_c
                ? `It leaves the home page. Anyone with the link can still open ${path} and order.`
                : 'It goes back on the home page with the rest of the store.'
            }
            busyLabel="Saving…"
            onConfirm={() =>
              toggle('show_on_home_c', !product.show_on_home_c, product.show_on_home_c ? 'Hidden from front page' : 'Back on the front page')
            }
          />
        )}
        <ConfirmAction
          label={product.active_c ? 'Pause sales' : 'Put on sale'}
          icon={product.active_c ? 'CirclePause' : 'CirclePlay'}
          title={product.active_c ? 'Pause sales?' : 'Put back on sale?'}
          description={
            product.active_c
              ? privatePlan
                ? 'New orders stop on the private page. Live lines keep running.'
                : 'New orders stop, on the front page and by direct link. Live lines keep running.'
              : 'New orders open again.'
          }
          busyLabel="Saving…"
          onConfirm={() => toggle('active_c', !product.active_c, product.active_c ? 'Sales paused' : 'On sale again')}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <Panel title={privatePlan ? 'Private page' : 'Product page'} icon={privatePlan ? 'Lock' : 'Link'} bodyClassName="space-y-3 py-4">
          <p className="break-all rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm">{url}</p>
          <div className="flex flex-wrap gap-2">
            <CopyButton text={url} label="Copy link" />
            <Button variant="outline" size="sm" asChild>
              <a href={path} target="_blank" rel="noreferrer">
                <ApperIcon name="ExternalLink" size={16} />
                Open page
              </a>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {privatePlan
              ? 'Invitation only. Never on the front page, in product listings or the sitemap, and search engines are asked to skip it. Share this link only with people you invite.'
              : product.show_on_home_c
                ? 'Listed on the front page and reachable by this link.'
                : 'Not on the front page. Only people with this link can find it, and search engines are asked to skip it.'}
          </p>
        </Panel>

        <Panel title="Photo" icon="Image" bodyClassName="py-4">
          <div className="flex aspect-video items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40">
            {product.image_url_c ? (
              <img src={product.image_url_c} alt={product.Name} className="h-full w-full object-cover" />
            ) : (
              <span className="flex flex-col items-center gap-1 text-xs text-muted-foreground">
                <ApperIcon name="ImageOff" size={20} />
                No photo
              </span>
            )}
          </div>
        </Panel>

        <Panel title="On the page" icon="FileText" bodyClassName="space-y-4 py-4" className="lg:col-span-2">
          {product.description_c ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{product.description_c}</p>
          ) : (
            <p className="text-sm text-muted-foreground">No description yet.</p>
          )}
          {features.length > 0 && (
            <ul className="space-y-1.5 text-sm">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <ApperIcon name="Check" size={16} className="text-primary" />
                  {f}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Details" icon="Info" className="lg:col-span-2">
          <dl className="divide-y divide-border">
            <DetailRow label="Address">
              <span className="font-mono">{path}</span>
            </DetailRow>
            {builtIn ? (
              <DetailRow label="Pricing">Uses the built-in plan prices and checkout options.</DetailRow>
            ) : (
              PRODUCT_TERMS.map((t) => (
                <DetailRow key={t.id} label={t.label}>
                  {productPrice(product, t.id) != null ? money(productPrice(product, t.id)) : 'Not sold'}
                </DetailRow>
              ))
            )}
            <DetailRow label={privatePlan ? 'Visibility' : 'Front page'}>
              <StatusBadge value={frontState(product)} styles={FRONT_STYLES} />
            </DetailRow>
            <DetailRow label="Sale">
              <StatusBadge value={saleState(product)} styles={SALE_STYLES} />
            </DetailRow>
            <DetailRow label="Order on page">{String(product.sort_c ?? 0)}</DetailRow>
            <DetailRow label="Last changed">{formatLocalDate(product.ModifiedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
          </dl>
        </Panel>
      </div>
    </div>
  );
}
